import { BOARD_PAGE_LIMIT_MAX, canEditBoard, type BoardKind, type BoardSummary } from '@blueprint/domain'
import { useEffect, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button } from '../../components/controls.js'
import { getRepository, useBoardStore } from '../../state/board-store.js'
import { useMindmapStore } from '../../state/mindmap-store.js'
import { useUiStore, type BoardNameDialogKind } from '../../state/ui-store.js'
import { useWorkflowStore } from '../../state/workflow-store.js'
import styles from './BoardsView.module.css'

/**
 * The switcher shared by the storymap, mindmap and workflow designers: every item in the
 * open item's project, by kind, to switch to — and, for someone who can edit the project,
 * to add a new one beside it. Managing items — renaming, deleting — belongs to the Boards view.
 */

const SECTIONS: { kind: BoardKind; title: string; create: BoardNameDialogKind; newLabel: string }[] = [
  { kind: 'storymap', title: 'Storymaps', create: 'create', newLabel: 'New storymap' },
  { kind: 'mindmap', title: 'Mindmaps', create: 'create-mindmap', newLabel: 'New mindmap' },
  { kind: 'workflow', title: 'Workflows', create: 'create-workflow', newLabel: 'New workflow' },
]

type Listing =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  /** `projectId` is null for an item reached by a share alone: then the list is what is shared with the user. */
  | { state: 'ready'; title: string; projectId: string | null; canCreate: boolean; items: BoardSummary[] }

/** The item open in the current designer, whichever kind it is. */
function openItemId(): string | null {
  const view = useUiStore.getState().view
  if (view === 'mindmap') return useMindmapStore.getState().id
  if (view === 'workflow') return useWorkflowStore.getState().id
  return useBoardStore.getState().activeBoardId
}

/** Every item in a scope, page by page: the switcher shows the whole project. */
async function listAll(scope: { projectId: string } | { shared: true }): Promise<BoardSummary[]> {
  const items: BoardSummary[] = []
  for (let offset = 0; ; offset += BOARD_PAGE_LIMIT_MAX) {
    const page = await getRepository().listBoards({ ...scope, limit: BOARD_PAGE_LIMIT_MAX, offset })
    items.push(...page.boards)
    if (page.boards.length === 0 || items.length >= page.total) return items
  }
}

async function loadListing(itemId: string): Promise<Listing> {
  const repository = getRepository()
  const [projectId, projects] = await Promise.all([repository.boardProject(itemId), repository.listProjects()])
  const project = projects.find((entry) => entry.id === projectId)
  if (project) {
    return { state: 'ready', title: project.name, projectId, canCreate: canEditBoard(project.role), items: await listAll({ projectId }) }
  }
  return { state: 'ready', title: 'Shared with me', projectId: null, canCreate: false, items: await listAll({ shared: true }) }
}

/** Saves the open mindmap or workflow and closes it; a storymap saves as it goes. */
async function leaveCurrent(): Promise<void> {
  const view = useUiStore.getState().view
  if (view === 'mindmap') await useMindmapStore.getState().leave()
  if (view === 'workflow') await useWorkflowStore.getState().leave()
}

/** Saves the open mindmap or workflow and keeps it open, in case the new item is cancelled. */
async function flushCurrent(): Promise<void> {
  const view = useUiStore.getState().view
  if (view === 'mindmap') await useMindmapStore.getState().flush()
  if (view === 'workflow') await useWorkflowStore.getState().flush()
}

export function BoardSwitcherDialog() {
  const open = useUiStore((state) => state.switcherOpen)
  const setSwitcherOpen = useUiStore((state) => state.setSwitcherOpen)
  const setView = useUiStore((state) => state.setView)
  const [listing, setListing] = useState<Listing>({ state: 'loading' })
  const [currentId, setCurrentId] = useState<string | null>(null)

  // Read afresh each time it opens, so items added or removed since are right.
  useEffect(() => {
    if (!open) return
    const itemId = openItemId()
    setCurrentId(itemId)
    setListing({ state: 'loading' })
    if (!itemId) return
    let cancelled = false
    loadListing(itemId)
      .then((next) => !cancelled && setListing(next))
      .catch((error: unknown) => !cancelled && setListing({ state: 'error', message: error instanceof Error ? error.message : 'The project could not be loaded.' }))
    return () => {
      cancelled = true
    }
  }, [open])

  const close = () => setSwitcherOpen(false)

  const choose = async (item: BoardSummary) => {
    close()
    if (item.id === currentId) return
    await leaveCurrent()
    if (item.kind === 'mindmap') {
      void useMindmapStore.getState().open(item.id)
      setView('mindmap')
    } else if (item.kind === 'workflow') {
      void useWorkflowStore.getState().open(item.id)
      setView('workflow')
    } else {
      void useBoardStore.getState().openBoard(item.id)
      setView('board')
    }
  }

  const create = async (kind: BoardNameDialogKind) => {
    if (listing.state !== 'ready' || !listing.projectId) return
    close()
    await flushCurrent()
    // New items go into the selected project: selecting this one puts the new item beside the open one.
    await useBoardStore.getState().selectProject(listing.projectId)
    useUiStore.getState().setBoardNameDialog(kind)
  }

  const allBoards = async () => {
    close()
    await leaveCurrent()
    void useBoardStore.getState().showBoardsPage({})
    setView('boards')
  }

  return (
    <Dialog
      open={open}
      title={listing.state === 'ready' ? `Switch — ${listing.title}` : 'Switch'}
      onClose={close}
      onSubmit={close}
      footer={
        <>
          <Button onClick={() => void allBoards()}>All boards…</Button>
          <Button variant="primary" onClick={close} data-autofocus>
            Close
          </Button>
        </>
      }
    >
      {listing.state === 'loading' ? <p className={styles.empty}>Loading…</p> : null}
      {listing.state === 'error' ? (
        <p className={styles.empty} role="alert">
          {listing.message}
        </p>
      ) : null}
      {listing.state === 'ready'
        ? SECTIONS.map((section) => {
            const items = listing.items.filter((item) => item.kind === section.kind)
            return (
              <section key={section.kind} data-testid={`switcher-${section.kind}`}>
                <div className={styles.switcherSection}>
                  <h3>{section.title}</h3>
                  {listing.canCreate ? <Button onClick={() => void create(section.create)}>+ {section.newLabel}</Button> : null}
                </div>
                {items.length === 0 ? (
                  <p className={styles.switcherNone}>None yet.</p>
                ) : (
                  <div className={styles.list}>
                    {items.map((item) => (
                      <div key={item.id} className={styles.row}>
                        <button type="button" className={styles.open} onClick={() => void choose(item)}>
                          <span className={styles.name}>{item.name}</span>
                          {item.id === currentId ? <span className={styles.active}>Open</span> : null}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )
          })
        : null}
    </Dialog>
  )
}
